import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';

// UTILITY
import { getSeasonRangeDate } from '@/utility/content';

// COMPONENTS
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';
import StageCarousel from '@/components/Carousels/StageSwiperCarousel/StageCarousel';

// HELP
import ServiceCard from './help/ServiceCard';
import EpisodeCard from './help/EpisodeCard';

// STYLES
import { Container, Select, MenuItem } from './styles';

const servicesCarouselOptions = {
  spaceBetween: 20,
  slidesPerView: 'auto',
  watchOverflow: true,
};

const episodesCarouselOptions = {
  spaceBetween: 30,
  watchOverflow: true,
  autoHeight: true,
  breakpoints: {
    0: {
      speed: 600,
      slidesPerView: 1,
      slidesPerGroup: 1,
    },
    600: {
      speed: 600,
      slidesPerView: 2,
      slidesPerGroup: 2,
    },
    900: {
      slidesPerView: 3,
      slidesPerGroup: 3,
    },
  },
};

const rentAppsKey = 'apps-rent-purchase';

function getSeasonData(seasons, curSeason) {
  const curSeasonData = seasons.find(season => season.season_number === curSeason);

  if (!curSeasonData) return { seasonApps: [], episodes: [] };

  const apps = curSeasonData.apps || [];
  const rentApps = curSeasonData[rentAppsKey] || [];
  const seasonApps = [...apps, ...rentApps];
  const episodes = curSeasonData.episodes || [];

  return { seasonApps, episodes };
}

// const Episodes = ({ onWatchNowClick, analyticsData, analyticsLocation }) => {
const Episodes = forwardRef((props, ref) => {
  const { onWatchNowClick, analyticsData, analyticsLocation } = props;
  const [t] = useTranslation();
  const episodesRef = useRef(null);

  const { streamingData } = useSelector(({ showDetails }) => ({
    streamingData: showDetails.streamingData,
  }));

  const [currentSeason, setCurrentSeason] = useState(1);
  const [swiper, setSwiper] = useState(null);

  const seasons = streamingData?.seasons || [];
  const hasSeasons = seasons.length;

  function selectCurrentEpisode() {
    let curSeason;
    let curEpisode;

    seasons.every(season => {
      curEpisode = season.episodes.find(episode => episode.is_current);
      curSeason = curEpisode ? season : undefined;

      return !curEpisode;
    });

    if (curSeason && currentSeason !== curSeason.season_number)
      setCurrentSeason(curSeason.season_number);

    if (swiper && curEpisode?.episode_number) swiper.slideTo(curEpisode.episode_number - 1);
  }

  useImperativeHandle(ref, () => ({
    selectCurrentEpisode: () => selectCurrentEpisode(),
    scrollIntoView: () =>
      episodesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  }));

  if (!hasSeasons) return null;

  const curSeasonData = getSeasonData(seasons, currentSeason);

  function handleSeasonChange(e) {
    const seasonNumber = e.target.value;

    setCurrentSeason(seasonNumber);
  }

  return (
    <Container ref={episodesRef} id="episodes" className="episodes">
      <div className="page-content">
        <Typography className="episodes__title" variant="h4">
          {t('episodes.title')}
        </Typography>
        <Select
          value={currentSeason}
          onChange={handleSeasonChange}
          autoWidth
          fullWidth={false}
          renderValue={curValue => (
            <Typography variant="h5" component="p">
              {t('common.seasonNumber', { number: curValue })}
            </Typography>
          )}
        >
          {seasons.map((season, index) => {
            const { season_number } = season;

            const title = t('common.seasonNumber', { number: season_number });

            const subtitle = getSeasonRangeDate(season.air_date, season.end_date);

            const desc =
              season.total_episodes &&
              t('common.episodesCount', {
                count: season.total_episodes,
                number: season.total_episodes,
              });

            return (
              <MenuItem value={season.season_number} key={index}>
                <div className="menu-item__left">
                  <Typography variant="body1">
                    {season.live && (
                      <Typography
                        className="m-r-10 text-color--purple"
                        variant="h6"
                        component="span"
                      >
                        {t('common.live')} !
                      </Typography>
                    )}
                    {title}
                    &nbsp;
                    {subtitle && (
                      <Typography component="span" color="textSecondary" className="p5">
                        ({subtitle})
                      </Typography>
                    )}
                  </Typography>
                </div>
                <div className="menu-item__right">
                  <Typography color="textSecondary" className="p5">
                    {desc}
                  </Typography>
                </div>
              </MenuItem>
            );
          })}
        </Select>
        <div className="episodes__services services">
          <Typography className="services__title" component="h5" variant="h4">
            {t('episodes.services.title')}
          </Typography>
          <StageCarousel
            className="services-carousel"
            carouselOptions={servicesCarouselOptions}
            stylesType="where-to-watch"
          >
            {curSeasonData.seasonApps.map((appData, index) => {
              return (
                <SwiperSlide key={index}>
                  <ServiceCard
                    data={appData}
                    onWatchNowClick={onWatchNowClick}
                    analyticsData={{ ...analyticsData, slot: index + 1 }}
                    location={analyticsLocation || `Slot ${index + 1}`}
                    key={index}
                  />
                </SwiperSlide>
              );
            })}
          </StageCarousel>
        </div>
        <div className="episodes__episodes">
          <StageCarousel
            className="episodes-carousel"
            carouselOptions={{ ...episodesCarouselOptions, onSwiper: setSwiper }}
            stylesType="where-to-watch"
          >
            {curSeasonData.episodes.map((episode, index) => {
              const firstSource = episode.sources.length && episode.sources[0];
              const firstApp = curSeasonData.seasonApps.length && curSeasonData.seasonApps[0];

              return (
                <SwiperSlide key={episode.episode_id}>
                  <EpisodeCard
                    data={episode}
                    seasonNumber={currentSeason}
                    link={firstSource.web_link || firstApp?.content_url}
                    onWatchNowClick={onWatchNowClick}
                    analyticsData={{
                      ...analyticsData,
                      app_id: firstApp.id,
                      service_name: firstApp.name,
                      is_affiliate: !!firstApp.is_affiliate,
                      slot: index + 1,
                    }}
                    location={analyticsLocation || `Slot ${index + 1}`}
                    key={index}
                  />
                </SwiperSlide>
              );
            })}
          </StageCarousel>
        </div>
      </div>
    </Container>
  );
});

Episodes.defaultProps = {
  onWatchNowClick: () => {},
  analyticsData: {},
  analyticsLocation: '',
};

Episodes.propTypes = {
  onWatchNowClick: PropTypes.func,
  analyticsData: PropTypes.object,
  analyticsLocation: PropTypes.string,
};

export default React.memo(Episodes);
